import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { Children, cloneElement, forwardRef, useRef, useState, useImperativeHandle, useEffect } from 'react';
import mitt from 'mitt';
import Popover from '../Popover';
import { composeEvents, pageViewChange } from '../utils';
import { useThemeProps } from '../system';
var emitter = mitt();
var globalTimeoutId = null;
var instantDelay = false;

function startGlobalTimeout() {
  instantDelay = true;
  globalTimeoutId = setTimeout(function () {
    instantDelay = false;
  }, 500);
}

function clearGlobalTimeout() {
  clearTimeout(globalTimeoutId);
}

function Tooltip(props, ref) {
  var _useThemeProps = useThemeProps('tooltip', props),
      children = _useThemeProps.children,
      _useThemeProps$openDe = _useThemeProps.openDelay,
      openDelay = _useThemeProps$openDe === void 0 ? 500 : _useThemeProps$openDe,
      _useThemeProps$closeD = _useThemeProps.closeDelay,
      closeDelay = _useThemeProps$closeD === void 0 ? 300 : _useThemeProps$closeD,
      _useThemeProps$defaul = _useThemeProps.defaultOpen,
      defaultOpen = _useThemeProps$defaul === void 0 ? false : _useThemeProps$defaul,
      _useThemeProps$keepIn = _useThemeProps.keepInView,
      keepInView = _useThemeProps$keepIn === void 0 ? true : _useThemeProps$keepIn,
      placement = _useThemeProps.placement,
      popoverProps = _useThemeProps.popoverProps,
      renderTo = _useThemeProps.renderTo,
      title = _useThemeProps.title,
      _useThemeProps$trigge = _useThemeProps.triggerOnFocus,
      triggerOnFocus = _useThemeProps$trigge === void 0 ? true : _useThemeProps$trigge,
      _useThemeProps$trigge2 = _useThemeProps.triggerOnHover,
      triggerOnHover = _useThemeProps$trigge2 === void 0 ? true : _useThemeProps$trigge2,
      childProps = _objectWithoutPropertiesLoose(_useThemeProps, ["children", "openDelay", "closeDelay", "defaultOpen", "keepInView", "placement", "popoverProps", "renderTo", "title", "triggerOnFocus", "triggerOnHover"]);

  var isPageInView = useRef(true);
  var isMouseDown = useRef(false);
  var openTimeoutId = useRef(null);
  var closeTimeoutId = useRef(null);

  var _useState = useState(defaultOpen),
      isPopoverOpen = _useState[0],
      setIsPopoverOpen = _useState[1];

  useEffect(function () {
    // prevents tooltips showing when focused and navigating back to a page after leaving
    var cleanupPageViewChange = pageViewChange(function (inView) {
      return setTimeout(function () {
        return isPageInView.current = inView;
      });
    });
    emitter.on('CLOSE_OPEN_TOOLTIPS', close);
    return function () {
      emitter.off('CLOSE_OPEN_TOOLTIPS', close);
      clearTimeout(openTimeoutId.current);
      clearTimeout(closeTimeoutId.current);
      cleanupPageViewChange();
    };
  }, []);

  var open = function open() {
    clearTimeout(closeTimeoutId.current);
    closeTimeoutId.current = null;
    setIsPopoverOpen(true);
  };

  var close = function close() {
    clearTimeout(openTimeoutId.current);
    openTimeoutId.current = null;
    setIsPopoverOpen(false);
  };

  var toggle = function toggle() {
    setIsPopoverOpen(!isPopoverOpen);
  };

  useImperativeHandle(ref, function () {
    return {
      open: open,
      close: close,
      toggle: toggle
    };
  });
  useEffect(function () {
    if (triggerOnFocus === false) {
      close();
    }
  }, [triggerOnFocus]);
  useEffect(function () {
    if (triggerOnHover === false) {
      close();
    }
  }, [triggerOnHover]);

  var createOpenTimeout = function createOpenTimeout() {
    clearGlobalTimeout();

    if (openTimeoutId.current === null) {
      clearTimeout(closeTimeoutId.current);
      closeTimeoutId.current = null;

      if (isPopoverOpen === false) {
        emitter.emit('CLOSE_OPEN_TOOLTIPS');
        openTimeoutId.current = setTimeout(function () {
          return open();
        }, instantDelay ? 0 : openDelay);
      }
    }
  };

  var createCloseTimeout = function createCloseTimeout() {
    startGlobalTimeout();

    if (closeTimeoutId.current === null) {
      clearTimeout(openTimeoutId.current);
      openTimeoutId.current = null;
      closeTimeoutId.current = setTimeout(function () {
        return close();
      }, closeDelay);
    }
  };

  var handleFocus = function handleFocus() {
    if (isPageInView.current && !isMouseDown.current) {
      open();
    }
  };

  var handleMouseDown = function handleMouseDown() {
    isMouseDown.current = true;
    close();
  };

  var handleMouseUp = function handleMouseUp() {
    isMouseDown.current = false;
  };

  var child = Children.only(children);
  var _child$props = child.props,
      onFocus = _child$props.onFocus,
      onBlur = _child$props.onBlur,
      onMouseEnter = _child$props.onMouseEnter,
      onMouseLeave = _child$props.onMouseLeave,
      onMouseDown = _child$props.onMouseDown,
      onMouseUp = _child$props.onMouseUp;

  if (!title) {
    return /*#__PURE__*/cloneElement(child, childProps);
  }

  if (triggerOnFocus) {
    childProps.onFocus = composeEvents(handleFocus, onFocus);
    childProps.onBlur = composeEvents(close, onBlur);
    childProps.onMouseDown = composeEvents(handleMouseDown, onMouseDown);
    childProps.onMouseUp = composeEvents(handleMouseUp, onMouseUp);
  }

  if (triggerOnHover) {
    childProps.onMouseEnter = composeEvents(createOpenTimeout, onMouseEnter);
    childProps.onMouseLeave = composeEvents(createCloseTimeout, onMouseLeave);
  }

  return /*#__PURE__*/React.createElement(Popover, _extends({
    shouldFlip: true,
    paddingHorizontal: 0.75,
    paddingVertical: 0.25,
    fontSize: 5,
    textAlign: "center",
    radius: 2,
    backgroundColor: "grey-9",
    color: "rgba(255,255,255,0.94)",
    zIndex: 9999
  }, popoverProps, {
    anchorElement: /*#__PURE__*/cloneElement(child, childProps),
    onMouseEnter: createOpenTimeout,
    onMouseLeave: createCloseTimeout,
    keepInView: keepInView,
    placement: placement,
    renderTo: renderTo,
    children: title,
    open: isPopoverOpen
  }));
}

Tooltip = /*#__PURE__*/forwardRef(Tooltip);
export default Tooltip;